import { useEffect, useMemo, useRef } from 'react';
import type { AnyExtension, Editor } from '@tiptap/core';
import { Markdown } from '@tiptap/markdown';
import { EditorContent, useEditor } from '@tiptap/react';
import { RichTextProvider } from 'reactjs-tiptap-editor';
import { RichTextBold } from 'reactjs-tiptap-editor/bold';
import { RichTextItalic } from 'reactjs-tiptap-editor/italic';
import { RichTextUnderline } from 'reactjs-tiptap-editor/textunderline';
import { RichTextStrike } from 'reactjs-tiptap-editor/strike';
import { RichTextHeading } from 'reactjs-tiptap-editor/heading';
import { RichTextBulletList } from 'reactjs-tiptap-editor/bulletlist';
import { RichTextOrderedList } from 'reactjs-tiptap-editor/orderedlist';
import { RichTextTaskList } from 'reactjs-tiptap-editor/tasklist';
import { RichTextBlockquote } from 'reactjs-tiptap-editor/blockquote';
import { RichTextCode } from 'reactjs-tiptap-editor/code';
import { RichTextCodeBlock } from 'reactjs-tiptap-editor/codeblock';
import { RichTextLink } from 'reactjs-tiptap-editor/link';
import { RichTextTable } from 'reactjs-tiptap-editor/table';
import { RichTextHighlight } from 'reactjs-tiptap-editor/highlight';
import { RichTextHorizontalRule } from 'reactjs-tiptap-editor/horizontalrule';
import { RichTextUndo, RichTextRedo } from 'reactjs-tiptap-editor/history';
import { RichTextImage } from 'reactjs-tiptap-editor/image';
import { Mention } from 'reactjs-tiptap-editor/mention';
import 'reactjs-tiptap-editor/style.css';
import 'react-image-crop/dist/ReactCrop.css';
import { useResolvedTheme } from '../use-theme';
import { cn } from '../../lib/utils';
import {
  editorContentFromStored,
  hydrateMentionTokens,
  isRichTextEmpty,
  mentionLabelForId,
  serializeMentions,
  type MentionLabel,
} from '../../lib/rich-text';
import { richTextBaseExtensions } from './rich-text-kit';
import RichTextSlashMenu from './RichTextSlashMenu';
import { imageFilesFromTransfer, rejectImageFile } from './image-upload-limits';

export type MentionSuggestion = MentionLabel;

const EMPTY_MENTIONS: MentionSuggestion[] = [];

function sameMarkdown(left: string, right: string): boolean {
  const norm = (value: string) => value.replace(/\r\n/g, '\n').trim();
  return norm(left) === norm(right);
}

function markdownNow(editor: Editor): string | null {
  if (typeof editor.getMarkdown !== 'function') return null;
  try {
    return editor.getMarkdown();
  } catch {
    return null;
  }
}

function editorHtml(editor: Editor | null): string | null {
  if (!editor || editor.isDestroyed) {
    return null;
  }
  try {
    if (editor.schema) {
      return serializeMentions(editor.getHTML());
    }
  } catch {
    // Vendor extensions bundle a second TipTap JSX runtime; getHTML() can throw.
  }
  const dom = editor.view?.dom;
  if (dom instanceof HTMLElement) {
    return serializeMentions(dom.innerHTML);
  }
  return null;
}

type Props = {
  value: string;
  onChange: (html: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  readOnly?: boolean;
  disabled?: boolean;
  variant?: 'full' | 'compact';
  placeholder?: string;
  mentions?: MentionSuggestion[];
  upload?: (file: File) => Promise<string>;
  onUploadError?: (message: string) => void;
  className?: string;
};

function insertUploadedImage(editor: Editor, src: string, alt: string, pos?: number): void {
  const chain = editor.chain().focus();
  if (typeof pos === 'number') chain.setTextSelection(pos);
  const withImage = chain as typeof chain & {
    setImageInline?: (attrs: { src: string; alt?: string }) => typeof chain;
  };
  if (typeof withImage.setImageInline === 'function') {
    withImage.setImageInline({ src, alt }).run();
    return;
  }
  chain.insertContent({ type: 'imageBlock', attrs: { src, alt } }).run();
}

export default function RichTextField({
  value,
  onChange,
  onBlur,
  onFocus,
  readOnly = false,
  disabled = false,
  variant = 'full',
  placeholder = "Write here. Press '/' for commands.",
  mentions = EMPTY_MENTIONS,
  upload,
  onUploadError,
  className,
}: Props) {
  const theme = useResolvedTheme();
  const locked = readOnly || disabled;
  const mentionsRef = useRef(mentions);
  const uploadRef = useRef(upload);
  const onChangeRef = useRef(onChange);
  const onBlurRef = useRef(onBlur);
  const onFocusRef = useRef(onFocus);
  const onUploadErrorRef = useRef(onUploadError);
  const editorRef = useRef<Editor | null>(null);
  mentionsRef.current = mentions;
  uploadRef.current = upload;
  onChangeRef.current = onChange;
  onBlurRef.current = onBlur;
  onFocusRef.current = onFocus;
  onUploadErrorRef.current = onUploadError;

  const extensions = useMemo(
    () =>
      [
        ...richTextBaseExtensions({
          placeholder,
          upload: (file: File) => {
            const fn = uploadRef.current;
            if (!fn) {
              return Promise.reject(new Error('Image upload is not available'));
            }
            return fn(file);
          },
        }),
        Markdown.configure({ indentation: { style: 'space', size: 2 } }),
        Mention.configure({
          suggestions: [
            {
              char: '@',
              items: async ({ query }: { query: string }) => {
                const q = query.toLowerCase();
                return mentionsRef.current
                  .filter((item) => item.label.toLowerCase().includes(q))
                  .slice(0, 8);
              },
            },
          ],
          renderHTML({ node }: { node: { attrs: { id: string; label?: string | null } } }) {
            const id = node.attrs.id;
            const label = node.attrs.label || mentionLabelForId(id, mentionsRef.current);
            return ['span', { 'data-type': 'mention', 'data-id': id, class: 'mention' }, `@${label}`];
          },
          renderText({ node }: { node: { attrs: { id: string; label?: string | null } } }) {
            const id = node.attrs.id;
            const label = node.attrs.label || mentionLabelForId(id, mentionsRef.current);
            return `@${label}`;
          },
        }),
      ] as AnyExtension[],
    [placeholder],
  );

  function queueImageFiles(data: DataTransfer | null, pos?: number): boolean {
    const current = editorRef.current;
    const uploadFn = uploadRef.current;
    if (!current || current.isDestroyed || !current.isEditable || !uploadFn) return false;
    const files = imageFilesFromTransfer(data);
    if (files.length === 0) return false;
    for (const file of files) {
      const rejection = rejectImageFile(file);
      if (rejection) {
        onUploadErrorRef.current?.(rejection);
        continue;
      }
      void uploadFn(file)
        .then((src) => {
          if (current.isDestroyed) return;
          insertUploadedImage(current, src, file.name.replace(/\.[^.]+$/, ''), pos);
        })
        .catch((err: unknown) => {
          onUploadErrorRef.current?.(err instanceof Error ? err.message : 'Image upload failed.');
        });
    }
    return true;
  }

  const mentionsKey = mentions.map((item) => `${item.id}:${item.label}`).join('|');
  const stored = editorContentFromStored(value || '');

  const editor = useEditor({
    immediatelyRender: false,
    shouldRerenderOnTransaction: false,
    extensions,
    content:
      stored.contentType === 'html' ? hydrateMentionTokens(stored.content, mentions) : stored.content,
    contentType: stored.contentType === 'markdown' ? 'markdown' : undefined,
    editable: !locked,
    editorProps: {
      attributes: {
        class: 'min-h-[96px] px-3 py-2 text-sm outline-none',
      },
      handlePaste: (_view, event) => {
        return queueImageFiles(event.clipboardData);
      },
      handleDrop: (view, event, _slice, moved) => {
        if (moved) return false;
        const pos = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos;
        return queueImageFiles(event.dataTransfer, pos);
      },
    },
    onCreate: ({ editor: next }) => {
      editorRef.current = next;
    },
    onUpdate: ({ editor: next }) => {
      const html = editorHtml(next);
      if (html !== null) {
        onChangeRef.current(html);
      }
    },
    onFocus: () => {
      onFocusRef.current?.();
    },
  });

  useEffect(() => {
    editorRef.current = editor ?? null;
  }, [editor]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editor.setEditable(!locked);
  }, [editor, locked]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const nextStored = editorContentFromStored(value || '');
    if (nextStored.contentType === 'markdown') {
      const currentMd = markdownNow(editor);
      if (currentMd === null || !sameMarkdown(currentMd, nextStored.content)) {
        editor.commands.setContent(nextStored.content, { contentType: 'markdown', emitUpdate: false });
      }
      const html = editorHtml(editor);
      if (html && mentions.length > 0 && /@user:[0-9a-fA-F-]{36}/.test(html)) {
        const hydrated = hydrateMentionTokens(html, mentionsRef.current);
        if (hydrated !== html) {
          editor.commands.setContent(hydrated, { emitUpdate: false });
        }
      }
      return;
    }
    const current = editorHtml(editor);
    if (current === null) return;
    if (isRichTextEmpty(value) && isRichTextEmpty(current)) {
      return;
    }
    const visible = editor.view?.dom instanceof HTMLElement ? editor.view.dom.innerHTML : '';
    const storageMatches = value === current;
    const stillShowingTokens = /@user:[0-9a-fA-F-]{36}/.test(visible);
    if (storageMatches && (!stillShowingTokens || mentions.length === 0)) {
      return;
    }
    const next = hydrateMentionTokens(nextStored.content, mentionsRef.current);
    if (next === current) {
      return;
    }
    editor.commands.setContent(next, { emitUpdate: false });
  }, [editor, value, mentionsKey, mentions.length]);

  if (!editor) {
    return <div className={cn('min-h-[96px] rounded-md border', className)} data-editor="true" />;
  }

  const compact = variant === 'compact';

  return (
    <div
      data-editor="true"
      data-variant={variant}
      className={cn(
        'overflow-hidden rounded-md border bg-background text-foreground',
        locked && 'opacity-70',
        className,
      )}
      onFocus={() => {
        onFocusRef.current?.();
      }}
      onBlur={(event) => {
        const next = event.relatedTarget;
        if (next instanceof Node && event.currentTarget.contains(next)) {
          return;
        }
        const fromDom =
          editor.view?.dom instanceof HTMLElement
            ? serializeMentions(editor.view.dom.innerHTML)
            : null;
        const html = fromDom ?? editorHtml(editor);
        if (html !== null) {
          onChangeRef.current(html);
        }
        onBlurRef.current?.();
      }}
    >
      <RichTextProvider editor={editor} dark={theme === 'dark'}>
        {locked ? null : (
          <div className="flex flex-wrap items-center gap-0.5 border-b px-1 py-1">
            <RichTextUndo />
            <RichTextRedo />
            {compact ? null : <RichTextHeading />}
            <RichTextBold />
            <RichTextItalic />
            {compact ? null : (
              <>
                <RichTextUnderline />
                <RichTextStrike />
              </>
            )}
            <RichTextBulletList />
            <RichTextOrderedList />
            {compact ? null : <RichTextTaskList />}
            <RichTextLink />
            {compact ? null : (
              <>
                <RichTextBlockquote />
                <RichTextCode />
                <RichTextCodeBlock />
                <RichTextTable />
                <RichTextHighlight />
                <RichTextHorizontalRule />
              </>
            )}
            <RichTextImage />
          </div>
        )}
        <RichTextSlashMenu />
        <EditorContent editor={editor} />
      </RichTextProvider>
    </div>
  );
}
